import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Button, TextField, Typography } from '@mui/material';

import { Container } from '../ReportsIndex/index.styles';
import { Box } from './index.styles';
import { IntuitiveCalendarPeriod } from 'src/views/components/IntuitiveCalendar';
import MethodologySelect from './MethodologySelect';
import { useReports } from 'src/Esg/hooks';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { QuestionnaireMethodology } from 'src/Esg/types';

export default function CreateESGReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reports, create } = useReports();

  const [yearConfirmationOpen, setYearConfirmationOpen] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const period = String(date?.getFullYear());

  const [questionnaireMethodology, setQuestionnaireMethodology] =
    useState<QuestionnaireMethodology>(
      QuestionnaireMethodology.ORIGINAL_QV3_2023_2024
    );

  const yearMatchingReports = reports.filter(
    (report) => report.period === String(date?.getFullYear())
  );
  const matchingReport = yearMatchingReports.find(
    (report) =>
      report.period === String(date?.getFullYear()) &&
      report.questionnaire_methodology === questionnaireMethodology
  );

  const _create = () =>
    create
      .mutateAsync({
        name,
        period,
        questionnaire_methodology: questionnaireMethodology,
      })
      .then((data) => {
        setYearConfirmationOpen(false);
        navigate(`/esg/reports/${data.id}/indicators`);
      });

  const handleCreateClick = () =>
    yearMatchingReports.length > 0 ? setYearConfirmationOpen(true) : _create();

  const isCreateDisabled =
    !!matchingReport || !name || !date || !questionnaireMethodology;

  return (
    <Container>
      <Typography variant="h1" sx={{ mb: '20px' }}>
        {t('esg.new-report')}
      </Typography>
      <Box>
        <TextField
          fullWidth
          label={t('esg.name-report')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <br />
        <br />
        <MethodologySelect
          value={questionnaireMethodology}
          onChange={(event) =>
            setQuestionnaireMethodology(
              event.target.value as QuestionnaireMethodology
            )
          }
        />
      </Box>
      {matchingReport && (
        <Alert severity="warning" sx={{ m: '20px 0px' }}>
          {t('esg.createReport.alerts.alreadyExists', {
            year: date?.getFullYear(),
            methodology: t(
              `esg.questionnaireMethodology.${questionnaireMethodology}`
            ),
          })}
        </Alert>
      )}
      <IntuitiveCalendarPeriod
        reportingPeriod="Y"
        selectedDate={date}
        setSelectedDate={(selDate: Date) => setDate(selDate)}
      />

      <Button
        sx={{ float: 'right', mt: '20px' }}
        size="large"
        disabled={isCreateDisabled}
        onClick={handleCreateClick}
      >
        {t('esg.create')}
      </Button>

      <ConfirmationModal
        open={yearConfirmationOpen}
        onClose={() => setYearConfirmationOpen(false)}
        onFalse={() => setYearConfirmationOpen(false)}
        textKey="esg.modal.1.subtitle"
        titleKey="esg.modal.1.title"
        falseButtonTextKey="esg.common.cancel"
        trueButtonTextKey="esg.modal.1.buttons.create"
        onTrue={_create}
      />
    </Container>
  );
}
